import React, { useRef, useState } from "react";
import { IoCloudUploadOutline } from "react-icons/io5";

interface FileUploadProps {
  onFileSelect: (file: File | null) => void;
  accept?: string;
  maxSizeMB?: number;
  label?: string;
  description?: string;
  className?: string;
}

export default function FileUpload({
  onFileSelect,
  accept = ".csv,.xls,.xlsx",
  maxSizeMB = 1.5,
  label = "Drag and Drop",
  description = `Maximum size of ${maxSizeMB}MB, CSV/Excel`,
  className = "",
}: FileUploadProps) {
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (selectedFile: File): boolean => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (selectedFile.size > maxSizeBytes) {
      alert(`File size exceeds ${maxSizeMB}MB`);
      return false;
    }
    return true;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0] || null;
    if (selectedFile && validateFile(selectedFile)) {
      setFile(selectedFile);
      onFileSelect(selectedFile);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && validateFile(droppedFile)) {
      setFile(droppedFile);
      onFileSelect(droppedFile);
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 px-6 py-10 text-center hover:border-indigo-400 ${className}`}
      onClick={handleClick}
    >
      <div className="mb-3 text-indigo-600">
        <IoCloudUploadOutline size={30} />
      </div>
      <p className="text-sm text-gray-700">
        <span className="font-medium">{label}</span> or{" "}
        <span className="font-medium text-indigo-600">Browse</span>
      </p>
      <p className="mt-1 text-xs text-gray-500">{description}</p>

      {file && (
        <p className="mt-3 text-sm font-medium text-gray-900">{file.name}</p>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handleFileChange}
      />
    </div>
  );
}
